import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  useBoolean,
  VStack,
} from "@chakra-ui/react";

import useTranslate from "hooks/useTranslate";
import useDressingRoom from "hooks/useDressingRoom";
import TinyHeading from "components/TinyHeading";
import TraitTile from "components/TraitTile";
import PayPopup from "components/PayPopup";
import { FaTimes } from "react-icons/fa";
import { BigNumber } from "ethers";
import useAssets from "hooks/useAssets";
import useVesting from "hooks/useVesting";
import { formatEtherBalance } from "utils/numberUtils";
import StakePopup from "components/StakePopup";
import useTraitSales from "hooks/useTraitSales";

const TraitsBagContainer = () => {
  const translate = useTranslate();
  const { traitSalesMap } = useTraitSales();
  const { tokenId, color, newTraits, initialTraits, removeTrait } =
    useDressingRoom();
  const [selectedTraits, setSelectedTraits] = useState<number[] | undefined>();
  const [isPayPopupOpen, setIsPayPopupOpen] = useBoolean();
  const [isStakePopupOpen, setIsStakePopupOpen] = useBoolean();
  const { honeyBalance } = useAssets();
  const { vestingAmountRemaining } = useVesting();
  const isMultiple = (newTraits?.length || 0) > 1;

  const handleBuy = (tokenId: number) => {
    setSelectedTraits([tokenId]);
    setIsPayPopupOpen.on();
  };

  const handleBuyAll = () => {
    setSelectedTraits(newTraits);
    setIsPayPopupOpen.on();
  };

  const ownedHoneyTotal = useMemo(
    () =>
      (honeyBalance || BigNumber.from("0")).add(
        vestingAmountRemaining || BigNumber.from("0")
      ),
    [honeyBalance, vestingAmountRemaining]
  );

  const totalPrice = useMemo(
    () =>
      newTraits?.reduce(
        (prev, tokenId) => prev.add(traitSalesMap[tokenId].price),
        BigNumber.from("0")
      ),
    [newTraits, traitSalesMap]
  );

  return tokenId && newTraits?.length && traitSalesMap ? (
    <>
      <VStack
        bg="dark.400"
        w="full"
        spacing="2px"
        pt="3"
        color="bright.500"
        align="flex-start"
      >
        <TinyHeading px="4" mb="3" mt="2px">
          {translate("cart:title")}
        </TinyHeading>

        {newTraits?.map((tokenId) => {
          const traitSale = traitSalesMap[tokenId];
          return traitSale ? (
            <Box key={tokenId} w="full">
              <TraitTile
                tokenId={tokenId}
                name={traitSale.name}
                price={traitSale.price}
                color={color}
                canvasSize={{
                  base: "14",
                  sm: "16",
                }}
              >
                <HStack spacing="3" p="1.5">
                  <Button
                    size="sm"
                    onClick={() => handleBuy(tokenId)}
                    isDisabled={
                      !ownedHoneyTotal ||
                      ownedHoneyTotal.lt(traitSale.price || "0")
                    }
                  >
                    {translate("common:buy")}
                  </Button>

                  <IconButton
                    size="sm"
                    aria-label=""
                    icon={<Icon as={FaTimes} />}
                    borderRadius="full"
                    colorScheme="dark"
                    onClick={() => removeTrait(tokenId)}
                  />
                </HStack>
              </TraitTile>
            </Box>
          ) : null;
        })}
      </VStack>

      {isMultiple && totalPrice && (
        <Box p="4" bg="dark.400">
          <Button
            w="full"
            onClick={handleBuyAll}
            isDisabled={
              !ownedHoneyTotal || !totalPrice || ownedHoneyTotal.lt(totalPrice)
            }
          >
            {translate("cart:buyAll", {
              count: newTraits.length,
              price: formatEtherBalance(totalPrice),
            })}
          </Button>
        </Box>
      )}

      {selectedTraits && color && (
        <PayPopup
          isOpen={isPayPopupOpen}
          tokenId={tokenId}
          newTraits={selectedTraits}
          color={color}
          onClose={setIsPayPopupOpen.off}
        />
      )}

      {selectedTraits && color && (
        <StakePopup
          isOpen={isStakePopupOpen}
          initialTraits={initialTraits}
          newTraits={selectedTraits}
          tokenId={tokenId}
          color={color}
          onClose={setIsStakePopupOpen.off}
        />
      )}
    </>
  ) : null;
};

export default TraitsBagContainer;
